import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";
import {
  convertFormFieldType,
  FormTypesWithValues,
  TellescopeFormField,
  QuestionGroupField,
  useTellescopeSinglePageForm,
} from "@tellescope/forms";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { cn, formatName } from "@tellescope/utils";

export interface SinglePageFormSubmissionProps {
  rootResponseId?: string;
  parentResponseId?: string;
  contactId: string;
  onSubmit: () => void;
  onSaveResponse: () => void;
  onCancel?: () => void;
  responseId?: string;
  formId?: string;
  className?: string;
}

export const SinglePageFormSubmission = ({
  onSubmit,
  onSaveResponse,
  onCancel,
  contactId,
  rootResponseId,
  parentResponseId,
  responseId,
  formId,
  className,
}: SinglePageFormSubmissionProps) => {
  const { t } = useTranslation("forms");
  const { session, selectedOrganization: org } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<TellescopeModelFromName<"forms">>();
  const [formSearch, setFormSearch] = useState("");
  const [debouncedFormSearch] = useDebouncedValue(formSearch);

  const formQuery = useTellescopeQuery({
    model: "forms",
    method: "getOne",
    request: formId ?? "",
    enabled: !!formId,
  });

  const formsQuery = useTellescopeInfiniteQuery({
    model: "forms",
    method: "getSome",
    request: {
      limit: 20,
      search: debouncedFormSearch ? { query: debouncedFormSearch } : undefined,
    },
    enabled: !formId,
    select: (data) => data.pages.flat(),
  });
  const isSearching = formsQuery.isFetching || formSearch !== debouncedFormSearch;
  const form = selectedForm ?? formQuery.data;

  const {
    visibleFields,
    currentResponses,
    getFieldProps,
    enduser,
    saveResponse,
    isComplete,
    submitForm,
    isSubmitted,
    isLoading,
    fieldsArr,
    logicOptions,
  } = useTellescopeSinglePageForm({
    formId: form?.id ?? "",
    parentResponseId,
    rootResponseId,
    enduserId: contactId,
    formTitle: form?.displayTitle || form?.title,
    responseId,
  });

  return (
    <div className={cn("w-full", className)}>
      <div className="flex flex-col gap-4 h-full w-full max-w-[800px] mx-auto px-6 py-3">
        <div className="flex flex-col gap-4">
          {!formId && (
            <div>
              <Label className="text-sm font-medium truncate">{t("Select Form")}</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                  >
                    {form ? (
                      <div className="flex flex-col gap-1 items-start overflow-hidden w-full text-left">
                        <div className="font-medium text-sm truncate w-full">{form.title}</div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{t("Choose a form to submit...")}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t("Search forms...")}
                      className="h-9"
                      value={formSearch}
                      onValueChange={setFormSearch}
                    />
                    <CommandList>
                      {isSearching ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">{t("Searching...")}</div>
                      ) : !formsQuery.data?.length ? (
                        <CommandEmpty>{t("No forms found")}</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {formsQuery.data.map((f) => (
                            <CommandItem
                              key={f.id}
                              value={f.id}
                              onSelect={() => {
                                setSelectedForm(f.id === form?.id ? undefined : f);
                                setOpen(false);
                              }}
                            >
                              <div className="flex flex-col gap-1 w-full">
                                <div className="font-medium text-sm truncate">{f.displayTitle || f.title}</div>
                                {!!f.description && (
                                  <div className="text-xs text-muted-foreground truncate">{f.description}</div>
                                )}
                              </div>
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4 shrink-0",
                                  form?.id === f.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          {!!form && isLoading && (
            <div className="flex justify-center items-center py-8">
              <Spinner />
            </div>
          )}
          {!!form && !isLoading && (
            <div className={cn("flex flex-col gap-4", !formId && "py-4 border-t border-t-gray-200")}>
              {visibleFields.map(({ id, type, answer }) => {
                const convertedType = convertFormFieldType(type);
                if (convertedType === null) {
                  return null;
                }

                switch (convertedType) {
                  case "Appointment Booking": {
                    if (!session) return null;
                    const value = answer.value;
                    if (typeof value !== "string" && typeof value !== "undefined") {
                      return null;
                    }

                    const props = getFieldProps(id, "Appointment Booking");
                    if (!props) return null;

                    return (
                      <TellescopeFormField<"Appointment Booking">
                        key={id}
                        {...props}
                        session={session}
                        enduserId={contactId}
                        organizationSubdomain={org?.subdomain || undefined}
                      />
                    );
                  }
                  case "Stripe": {
                    const value = answer.value;
                    if (typeof value !== "string" && typeof value !== "undefined") {
                      return null;
                    }
                    const formResponses = Object.values(currentResponses);

                    const props = getFieldProps(id, "Stripe");
                    if (!props) return null;

                    return (
                      <TellescopeFormField<"Stripe">
                        key={id}
                        {...props}
                        enduserId={contactId}
                        form={form}
                        formResponses={formResponses}
                      />
                    );
                  }
                  case "signature": {
                    const props = getFieldProps(id, "signature");
                    if (!props) return null;

                    return (
                      <TellescopeFormField<"signature">
                        key={id}
                        {...props}
                        fullName={enduser ? formatName(enduser) : ""}
                      />
                    );
                  }
                  case "Bridge Eligibility": {
                    const props = getFieldProps(id, "Bridge Eligibility");
                    if (!props) return null;

                    return (
                      <TellescopeFormField<"Bridge Eligibility">
                        key={id}
                        {...props}
                        enduserId={contactId}
                        enduser={enduser}
                        priorResponses={Object.values(currentResponses)}
                        sessionType="user"
                      />
                    );
                  }
                  case "Candid Eligibility": {
                    const props = getFieldProps(id, "Candid Eligibility");
                    if (!props) return null;

                    return (
                      <TellescopeFormField<"Candid Eligibility">
                        key={id}
                        {...props}
                        enduserId={contactId}
                        enduser={enduser}
                        priorResponses={Object.values(currentResponses)}
                        sessionType="user"
                      />
                    );
                  }
                  case "Question Group": {
                    const field = fieldsArr.find((f) => f.id === id);
                    if (!field) return null;
                    return (
                      <QuestionGroupField
                        key={id}
                        id={id}
                        title={field.title}
                        options={field.options}
                        allFields={fieldsArr}
                        currentResponses={currentResponses}
                        getFieldProps={getFieldProps}
                        logicOptions={logicOptions}
                        enduser={enduser}
                        session={session}
                        form={form}
                        formResponses={Object.values(currentResponses)}
                        enduserId={contactId}
                        organizationSubdomain={org?.subdomain || undefined}
                      />
                    );
                  }
                  default: {
                    const limitedType: Exclude<
                      FormTypesWithValues,
                      "Appointment Booking" | "Stripe" | "Bridge Eligibility" | "Candid Eligibility"
                    > = convertedType;
                    const props = getFieldProps<typeof limitedType>(id, limitedType);
                    if (!props) return null;

                    // @ts-expect-error Union type has been reduced, and TS doesn't like it
                    return <TellescopeFormField<typeof limitedType> key={id} {...props} />;
                  }
                }
              })}
              {!!visibleFields.length && (
                <div className="flex justify-end gap-2">
                  {!!onCancel && (
                    <Button variant="outline" onClick={onCancel}>
                      {t("Cancel")}
                    </Button>
                  )}
                  {isSubmitted ? (
                    <Button
                      onClick={async () => {
                        try {
                          await saveResponse();
                        } catch {
                          toast.error(t("Failed to save response. Please try again."));
                          return;
                        }
                        onSaveResponse();
                      }}
                    >
                      {t("Update Response")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={async () => {
                          try {
                            await saveResponse();
                          } catch {
                            toast.error(t("Failed to save draft. Please try again."));
                            return;
                          }
                          onSaveResponse();
                        }}
                        variant="outline"
                      >
                        {t("Save Draft")}
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            await submitForm();
                          } catch {
                            toast.error(t("Failed to submit form. Please try again."));
                            return;
                          }
                          onSubmit();
                        }}
                        disabled={!isComplete}
                      >
                        {t("Submit")}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {!!onCancel && !form && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={onCancel}>
                {t("Cancel")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
